import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';
import {
  IWheelOfFortuneHistoryResponse,
  IWheelOfFortuneResponse,
  WheelKind,
} from 'models/wheel-of-fortune/i-wheel-of-fortune';

import { addErrorMessageAction } from 'actions/global-actions';

import { getWheelOfFortuneHistoryService, getWheelOfFortuneService } from 'services/wheel-of-fortune.service';

import { queryKeys } from './query-keys';

/**
 * Cache key for one wheel's payload, for the wallet that owns it.
 *
 * `queryKeys.wheelOfFortune.data` cannot see the connected address, but everything in the
 * payload is per-user — spin balance, next spin number, free-spin state — so without the
 * wallet in the key an account switch would keep serving the previous user's numbers out
 * of cache, silently. It stays a suffix of the shared key so the `wheelOfFortune.all`
 * prefix invalidations elsewhere (spin purchase, VIP reward claims) still match it.
 */
export const wheelOfFortuneDataKey = (kind: WheelKind, walletAddress: string) =>
  [...queryKeys.wheelOfFortune.data(kind), walletAddress] as const;

interface UseWheelOfFortuneQueryOptions {
  page?: number;
  pageSize?: number;
  /** Which wheel to read. Defaults to the regular one. */
  kind?: WheelKind;
  /**
   * Whether a failure should raise a toast. Default true.
   *
   * False for a SECONDARY read — the modal subscribes to the wheel you are not looking at
   * purely to print its spin count on the switch. That number going missing is not worth
   * interrupting someone who never opens that tab, and with two subscriptions mounted a
   * shared outage would otherwise toast twice.
   */
  reportErrors?: boolean;
}

interface UseWheelOfFortuneQueryReturn {
  data: IWheelOfFortuneResponse | null;
  historyData: IWheelOfFortuneHistoryResponse | null;
  loading: boolean;
  historyLoading: boolean;
  error: unknown;
  historyError: unknown;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalPages: number;
  refresh: () => void;
}

const fetchWheelOfFortune = async (kind: WheelKind): Promise<IWheelOfFortuneResponse> => {
  const response = await getWheelOfFortuneService(new AbortController().signal, kind);
  return response.data;
};

const fetchWheelOfFortuneHistory = async (page: number, pageSize: number): Promise<IWheelOfFortuneHistoryResponse> => {
  const response = await getWheelOfFortuneHistoryService(new AbortController().signal, page, pageSize);
  return response.data;
};

export const useWheelOfFortuneQuery = (options: UseWheelOfFortuneQueryOptions = {}): UseWheelOfFortuneQueryReturn => {
  const { page = 1, pageSize = 10, kind = 'regular', reportErrors = true } = options;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const walletAddress = useSelector((state: IState): string => state.wallet.address);

  // Query for wheel of fortune data
  const {
    data = null,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: wheelOfFortuneDataKey(kind, walletAddress),
    queryFn: () => fetchWheelOfFortune(kind),
  });

  // Query for wheel of fortune history
  const {
    data: historyData = null,
    isLoading: historyLoading,
    error: historyError,
  } = useQuery({
    queryKey: queryKeys.wheelOfFortune.history(page, pageSize),
    queryFn: () => fetchWheelOfFortuneHistory(page, pageSize),
  });

  // Handle errors
  useEffect(() => {
    if (error && reportErrors) {
      const errorMessage =
        (error as any)?.response?.data?.message || error?.message || 'Failed to fetch wheel of fortune data';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [error, dispatch, reportErrors]);

  useEffect(() => {
    if (historyError && reportErrors) {
      const errorMessage =
        (historyError as any)?.response?.data?.message ||
        historyError?.message ||
        'Failed to fetch wheel of fortune history';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [historyError, dispatch, reportErrors]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: wheelOfFortuneDataKey(kind, walletAddress) });
    queryClient.invalidateQueries({ queryKey: queryKeys.wheelOfFortune.history(page, pageSize) });
  };

  const hasNextPage = Boolean(historyData?.next);
  const hasPreviousPage = Boolean(historyData?.previous);
  const totalPages = historyData?.count ? Math.ceil(historyData.count / pageSize) : 0;

  return {
    data,
    historyData,
    loading,
    historyLoading,
    error,
    historyError,
    hasNextPage,
    hasPreviousPage,
    totalPages,
    refresh,
  };
};
